import { defineStore } from 'pinia'
import {
  RechercheApi,
  RechercheControllerRechercheTypeRechercheEnum as RechercheTypeRechercheEnum,
  RechercheControllerRechercheTypeClientEnum as RechercheTypeClientEnum
} from '../api/api/recherche-api'

import type {
  ResultatRechercheDto
} from '../api/models'
import { Configuration } from '@/api/configuration'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.callaw.avoloi.com'
const apiConfig = new Configuration({
  basePath: API_BASE_URL,
})

// Types pour les critères de recherche
interface CriteresRecherche {
  typeRecherche: 'avocat' | 'cabinet'
  uuid: string[]
  idsDomaine: number[]
  idsSousDomaine: number[]
  codePostal: string
  typeClient: 'particulier' | 'entreprise' | null
  diag: boolean
  aideJuridictionnelle: boolean
  idsLangue: number[]
  limite: number
  page: number
}

// Types pour la pagination interne
interface PaginationState {
  page: number
  limite: number
  total: number
  totalPages: number
}

// Types pour l'historique de recherche
interface HistoryItem {
  criteres: CriteresRecherche
  resultats: ResultatRechercheDto[]
  pagination: PaginationState
  timestamp: number
}

// Type pour l'état du store
interface RechercheState {
  criteres: CriteresRecherche
  resultats: ResultatRechercheDto[]
  pagination: PaginationState
  loading: boolean
  error: string | null
  searchHistory: HistoryItem[]
}

export const useRechercheStore = defineStore('recherche', {
  state: (): RechercheState => ({
    // Critères de recherche actuels
    criteres: {
      typeRecherche: 'cabinet', // 'avocat' ou 'cabinet'
      uuid: [],
      idsDomaine: [],
      idsSousDomaine: [],
      codePostal: '',
      typeClient: null, // 'particulier', 'entreprise' ou null
      diag: false,
      aideJuridictionnelle: false,
      idsLangue: [],
      limite: 20,
      page: 1
    },

    // Résultats de recherche
    resultats: [],
    pagination: {
      page: 1,
      limite: 20,
      total: 0,
      totalPages: 0
    },

    // État de chargement
    loading: false,
    error: null,

    // Historique des recherches (pour navigation back)
    searchHistory: []
  }),

  getters: {
    // Vérifier si une recherche peut être lancée
    canSearch: (state): boolean => {
      return state.criteres.codePostal.length >= 5 ||
        state.criteres.idsDomaine.length > 0 ||
        state.criteres.idsSousDomaine.length > 0
    },

    // Obtenir les critères actifs pour affichage
    criteresActifs: (state): Partial<CriteresRecherche> => {
      const actifs: Partial<CriteresRecherche> = {}

      if (state.criteres.uuid.length > 0) actifs.uuid = state.criteres.uuid
      if (state.criteres.codePostal) actifs.codePostal = state.criteres.codePostal
      if (state.criteres.idsDomaine.length > 0) actifs.idsDomaine = state.criteres.idsDomaine
      if (state.criteres.idsSousDomaine.length > 0) actifs.idsSousDomaine = state.criteres.idsSousDomaine
      if (state.criteres.typeClient) actifs.typeClient = state.criteres.typeClient
      if (state.criteres.diag) actifs.diag = true
      if (state.criteres.aideJuridictionnelle) actifs.aideJuridictionnelle = true
      if (state.criteres.idsLangue.length > 0) actifs.idsLangue = state.criteres.idsLangue

      return actifs
    },

    // Compter le nombre de résultats
    nombreResultats: (state): number => state.pagination.total || 0
  },

  actions: {
    // Mettre à jour les critères de recherche
    updateCriteres(nouveauxCriteres: Partial<CriteresRecherche>): void {
      this.criteres = { ...this.criteres, ...nouveauxCriteres }
      // Remettre à la page 1 si on change les critères
      if (nouveauxCriteres.page === undefined) {
        this.criteres.page = 1
      }
    },

    // Réinitialiser les critères
    resetCriteres(): void {
      this.criteres = {
        typeRecherche: 'cabinet',
        uuid: [],
        idsDomaine: [],
        idsSousDomaine: [],
        codePostal: '',
        typeClient: null,
        diag: false,
        aideJuridictionnelle: false,
        idsLangue: [],
        limite: 20,
        page: 1
      }
      this.resultats = []
      this.pagination = {
        page: 1,
        limite: 20,
        total: 0,
        totalPages: 0
      }
    },

    // Lancer une recherche
    async search(nouveauxCriteres: Partial<CriteresRecherche> = {}): Promise<void> {
      // Mettre à jour les critères si fournis
      if (Object.keys(nouveauxCriteres).length > 0) {
        this.updateCriteres(nouveauxCriteres)
      }

      if (!this.canSearch) {
        this.error = 'Veuillez saisir au moins un code postal ou sélectionner un domaine de compétence'
        return
      }

      this.loading = true
      this.error = null

      try {
        const api = new RechercheApi(apiConfig)

        // Convertir le type de recherche vers l'enum API
        const typeRechercheEnum = this.criteres.typeRecherche === 'avocat'
          ? RechercheTypeRechercheEnum.Avocat
          : RechercheTypeRechercheEnum.Cabinet

        // Convertir le type de client vers l'enum API ou undefined
        let typeClientEnum: RechercheTypeClientEnum | undefined = undefined
        if (this.criteres.typeClient === 'particulier') {
          typeClientEnum = RechercheTypeClientEnum.Particulier
        } else if (this.criteres.typeClient === 'entreprise') {
          typeClientEnum = RechercheTypeClientEnum.Entreprise
        }

        // Préparer les paramètres pour l'API
        const params = {
          typeRecherche: typeRechercheEnum,
          uuidAvocat: this.criteres.typeRecherche === 'avocat' ? this.criteresActifs.uuid?.map(String) : undefined,
          uuidCabinet: this.criteres.typeRecherche === 'cabinet' ? this.criteresActifs.uuid?.map(String) : undefined,
          idsDomaine: this.criteresActifs.idsDomaine,
          idsSousDomaine: this.criteresActifs.idsSousDomaine,
          codePostal: this.criteresActifs.codePostal,
          typeClient: typeClientEnum,
          diag: this.criteresActifs.diag,
          aideJuridictionnelle: this.criteresActifs.aideJuridictionnelle,
          idsLangue: this.criteresActifs.idsLangue,
          limite: this.criteres.limite,
          page: this.criteres.page
        }

        const response = await api.rechercheControllerRecherche(
          params.typeRecherche,
          params.limite,
          params.page,
          undefined, // uuid
          params.uuidAvocat?.join(',') || undefined,
          params.uuidCabinet?.join(',') || undefined,
          params.idsDomaine,
          params.idsSousDomaine,
          undefined, // idsBar
          undefined, // idsReseau
          params.codePostal,
          params.typeClient,
          params.diag,
          params.aideJuridictionnelle,
          params.idsLangue,
          undefined, // urlProfil
          undefined, // tri
          undefined // ordre
        )

        // Les résultats sont directement dans response.data.data selon le modèle API
        this.resultats = response.data.data || []

        // Adapter la structure de pagination API vers notre store
        if (response.data.pagination) {
          this.pagination = {
            page: response.data.pagination.pageCourante || 1,
            limite: this.criteres.limite,
            total: response.data.pagination.nombreTotal || 0,
            totalPages: Math.ceil((response.data.pagination.nombreTotal || 0) / this.criteres.limite)
          }
        }

        // Sauvegarder dans l'historique
        this.saveToHistory()

      } catch (error) {
        console.error('Erreur lors de la recherche:', error)
        this.error = 'Erreur lors de la recherche. Veuillez réessayer.'
        this.resultats = []
      } finally {
        this.loading = false
      }
    },

    // Recherche par UUID (recherche rapide)
    async searchByUuid(uuids: string | string[], typeRecherche: 'avocat' | 'cabinet' = 'cabinet'): Promise<void> {
      this.updateCriteres({
        uuid: Array.isArray(uuids) ? uuids : [uuids],
        typeRecherche
      })

      await this.search()
    },

    // Aller à la page suivante
    async nextPage(): Promise<void> {
      if (this.pagination.page < this.pagination.totalPages) {
        this.updateCriteres({ page: this.pagination.page + 1 })
        await this.search()
      }
    },

    // Aller à la page précédente
    async previousPage(): Promise<void> {
      if (this.pagination.page > 1) {
        this.updateCriteres({ page: this.pagination.page - 1 })
        await this.search()
      }
    },

    // Aller à une page spécifique
    async goToPage(page: number): Promise<void> {
      this.updateCriteres({ page })
      await this.search()
    },

    // Charger plus de résultats (ajouter à la liste existante)
    async loadMore(): Promise<void> {
      if (this.pagination.page >= this.pagination.totalPages) {
        return // Pas de page suivante disponible
      }

      this.loading = true
      this.error = null

      try {
        const api = new RechercheApi(apiConfig)
        const nextPage = this.pagination.page + 1

        // Convertir le type de recherche vers l'enum API
        const typeRechercheEnum = this.criteres.typeRecherche === 'avocat'
          ? RechercheTypeRechercheEnum.Avocat
          : RechercheTypeRechercheEnum.Cabinet

        // Convertir le type de client vers l'enum API ou undefined
        let typeClientEnum: RechercheTypeClientEnum | undefined = undefined
        if (this.criteres.typeClient === 'particulier') {
          typeClientEnum = RechercheTypeClientEnum.Particulier
        } else if (this.criteres.typeClient === 'entreprise') {
          typeClientEnum = RechercheTypeClientEnum.Entreprise
        }

        // Préparer les paramètres pour l'API avec la page suivante
        const params = {
          typeRecherche: typeRechercheEnum,
          uuidAvocat: this.criteres.typeRecherche === 'avocat' ? this.criteresActifs.uuid?.map(String) : undefined,
          uuidCabinet: this.criteres.typeRecherche === 'cabinet' ? this.criteresActifs.uuid?.map(String) : undefined,
          idsDomaine: this.criteresActifs.idsDomaine,
          idsSousDomaine: this.criteresActifs.idsSousDomaine,
          codePostal: this.criteresActifs.codePostal,
          typeClient: typeClientEnum,
          diag: this.criteresActifs.diag,
          aideJuridictionnelle: this.criteresActifs.aideJuridictionnelle,
          idsLangue: this.criteresActifs.idsLangue,
          limite: this.criteres.limite,
          page: nextPage
        }

        const response = await api.rechercheControllerRecherche(
          params.typeRecherche,
          params.limite,
          params.page,
          undefined, // uuid
          params.uuidAvocat?.join(',') || undefined,
          params.uuidCabinet?.join(',') || (undefined as unknown as string),
          params.idsDomaine,
          params.idsSousDomaine
        )

        // Ajouter les nouveaux résultats à la liste existante
        const nouveauxResultats = response.data.data || []
        this.resultats = [...this.resultats, ...nouveauxResultats]

        // Mettre à jour la pagination
        if (response.data.pagination) {
          this.pagination = {
            ...this.pagination,
            page: response.data.pagination.pageCourante || nextPage
          }
        }

        // Mettre à jour les critères pour refléter la page courante
        this.criteres.page = nextPage

      } catch (error) {
        console.error('Erreur lors du chargement de plus de résultats:', error)
        this.error = 'Erreur lors du chargement. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },

    // Sauvegarder dans l'historique
    saveToHistory(): void {
      const historyItem: HistoryItem = {
        criteres: { ...this.criteres },
        resultats: [...this.resultats],
        pagination: { ...this.pagination },
        timestamp: Date.now()
      }

      this.searchHistory.unshift(historyItem)

      // Garder seulement les 10 dernières recherches
      if (this.searchHistory.length > 10) {
        this.searchHistory = this.searchHistory.slice(0, 10)
      }
    },

    // Restaurer une recherche depuis l'historique
    restoreFromHistory(index: number): void {
      if (this.searchHistory[index]) {
        const historyItem = this.searchHistory[index]
        this.criteres = { ...historyItem.criteres }
        this.resultats = [...historyItem.resultats]
        this.pagination = { ...historyItem.pagination }
      }
    }
  }
}) 